import { DOCUMENT, inject, Injectable, RendererFactory2, RendererStyleFlags2 } from '@angular/core';

const CLARITY_FLY_IN_MAX_INLINE_SIZE_PROPERTY = '--eff-clarity-fly-in-max-inline-size';

/**
 * Publishes the narrowest non-zero width of the registered elements, in px, as `--eff-clarity-fly-in-max-inline-size`
 * on the document's root element; skips a write that would not change the value and removes the property when no
 * element is left.
 */
@Injectable({ providedIn: 'root' })
export class ClarityFlyInMaxWidths {
	private readonly root = inject(DOCUMENT).documentElement;
	private readonly renderer = inject(RendererFactory2).createRenderer(null, null);
	private readonly elements = new Set<HTMLElement>();

	private published: string | null = null;

	public add(element: HTMLElement): void {
		this.elements.add(element);
		this.publish();
	}

	public remove(element: HTMLElement): void {
		this.elements.delete(element);
		this.publish();
	}

	/** Measures every mark again and publishes the narrowest non-zero width, or removes the property when none is left. */
	public publish(): void {
		if (this.elements.size === 0) {
			this.published = null;
			this.renderer.removeStyle(this.root, CLARITY_FLY_IN_MAX_INLINE_SIZE_PROPERTY, RendererStyleFlags2.DashCase);

			return;
		}

		const widths = Array.from(this.elements, element => Math.round(element.getBoundingClientRect().width)).filter(
			width => width > 0,
		);

		if (widths.length === 0) {
			return;
		}

		const value = `${Math.min(...widths)}px`;

		if (value === this.published) {
			return;
		}

		this.published = value;
		this.renderer.setStyle(this.root, CLARITY_FLY_IN_MAX_INLINE_SIZE_PROPERTY, value, RendererStyleFlags2.DashCase);
	}
}
